import React, { useCallback, useMemo } from 'react';

import { Box, useTheme } from '@mui/material';
import { useMatch } from 'react-router';

import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import { isSameSymbol, isSameSymbolIgnoringPeriod } from '@/entities/symbol';
import {
  requestForwardCurveSelection,
  requestSpotSelection,
  SELECTOR_MODES
} from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import ForwardCatalogCell from '@/pages/Catalog/CatalogTable/CatalogTableRow/ForwardCatalogCell';
import SpotCatalogCell from '@/pages/Catalog/CatalogTable/CatalogTableRow/SpotCatalogCell';
import usePermission from '@/shared/hooks/usePermission';
import type { CatalogItem } from '@/types/catalog';

import { parseForwardCurveRowToSymbol } from '../../utils/parseForwardCurveRowToSymbol';

import type { Cell, Row } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';

interface CatalogTableRowProps {
  virtualRow: VirtualItem;
  row: Row<CatalogItem>;
  isForwardCurves: boolean;
}

const CatalogTableRow = React.memo(function CatalogTableRow({
  virtualRow,
  row,
  isForwardCurves
}: CatalogTableRowProps) {
  const { palette } = useTheme();
  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewCatalogDetails);

  const isRestrictedData = useMemo(() => {
    if (!isPermissionGranted) {
      return true;
    }
    if (isForwardCurves) {
      return false;
    }
    const { Last, Change } = row.original;
    return !Last && !Change;
  }, [isForwardCurves, row.original, isPermissionGranted]);

  const symbol = useMemo(() => row.getValue<SymbolKey | undefined>('Symbol'), [row]);

  // Memoize calculation for forward curve symbols
  const symbols = useMemo((): SymbolKey[] => {
    if (!isForwardCurves) {
      return [];
    }
    return parseForwardCurveRowToSymbol(row);
  }, [isForwardCurves, row]);

  // Derive per-row selection booleans from Redux to avoid table-wide rerenders
  const selectorMode = useAppSelector(state => state.symbolSelector.selectorMode);
  const isForwardMode = selectorMode === SELECTOR_MODES.FORWARD_CURVE;
  const isSpotSelected = useAppSelector(state => {
    if (!symbol) return false;
    const equals = isForwardMode ? isSameSymbolIgnoringPeriod : isSameSymbol;
    return state.symbolSelector.symbols.some(s => equals(s, symbol));
  });
  const isAnyForwardSelected = useAppSelector(state => {
    if (!isForwardCurves || symbols.length === 0) return false;
    const equals = isForwardMode ? isSameSymbolIgnoringPeriod : isSameSymbol;
    const selected = state.symbolSelector.symbols;
    // Check if any of this row's forward symbols is selected
    for (const sym of symbols) {
      for (const s of selected) {
        if (equals(s, sym)) return true;
      }
    }
    return false;
  });

  // Memoize background color calculation using the booleans above
  const backgroundColor = useMemo(() => {
    if (isForwardCurves) {
      if (isAnyForwardSelected) {
        return palette.table.forwardCurve;
      }
    } else if (isSpotSelected) {
      return palette.table.spot;
    }

    if (isRestrictedData) {
      return palette.table.infoRowBg;
    }
    return virtualRow.index % 2 === 0 ? palette.secondary.main : palette.background.paper;
  }, [
    isForwardCurves,
    isAnyForwardSelected,
    isSpotSelected,
    isRestrictedData,
    virtualRow.index,
    palette
  ]);

  const dispatch = useAppDispatch();

  const handleRowClick = useCallback(() => {
    if (isRestrictedData) return;
    if (isForwardCurves) {
      if (!symbols.length) return;
      dispatch(requestForwardCurveSelection({ symbols }));
    } else if (symbol) {
      dispatch(requestSpotSelection({ symbol }));
    }
  }, [dispatch, isForwardCurves, isRestrictedData, symbol, symbols]);

  // Resolve once per row whether we're in dashboard route to avoid per-cell router hooks
  const isInDashboard = Boolean(useMatch('/dashboard/*'));

  // Memoize cell rendering logic if needed, though map usually handles this okay
  const renderCell = (cell: Cell<CatalogItem, unknown>) => {
    const cellKey = `${row.id}-${cell.id}`; // Combine row and cell id for key
    if (isForwardCurves) {
      return (
        <ForwardCatalogCell
          key={cellKey}
          cell={cell}
          isInDashboard={isInDashboard}
          isRestrictedData={isRestrictedData}
          symbols={symbols}
        />
      );
    }
    return (
      <SpotCatalogCell
        key={cellKey}
        cell={cell}
        isInDashboard={isInDashboard}
        isRestrictedData={isRestrictedData}
        symbol={symbol}
      />
    );
  };

  return (
    <Box
      key={row.id}
      data-index={virtualRow.index}
      sx={{
        display: 'flex',
        position: 'absolute',
        top: 0,
        left: 0,
        transform: `translateY(${virtualRow.start}px)`,
        width: '100%',
        height: `${virtualRow.size}px`,
        padding: 0,
        paddingLeft: '8px',
        backgroundColor,
        cursor: isRestrictedData ? 'not-allowed' : 'pointer',
        ...(isRestrictedData
          ? {}
          : {
              '&:hover': {
                backgroundColor: 'table.highlight'
              }
            })
      }}
      onClick={handleRowClick}
    >
      {row.getVisibleCells().map(renderCell)}
    </Box>
  );
});

export default CatalogTableRow;
